/**
 * Página pública de chat do fluxo
 * Acessível via /chat/:slug - Sem autenticação
 * Renderiza visual idêntico ao preview do FlowTheme
 * Suporta todos os 8 templates: WhatsApp, Instagram, TikTok, Messenger, Telegram, Discord, Slack, UaizyChat
 * Layout: Header full-width + Input inline estilo Typebot
 * Suporta mídia: imagem, vídeo, áudio, documento, lista e botões
 */

import { useParams } from 'react-router-dom';
import { normalizeEmbedUrl } from '@/lib/embed-utils';
import { formatWhatsAppText } from '@/lib/format-whatsapp';
import { useState, useRef, useEffect } from 'react';
import { 
  Send, 
  Bot, 
  User, 
  Loader2, 
  MessageCircle, 
  AlertCircle,
  ArrowLeft,
  Video,
  Phone,
  MoreVertical,
  Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePublicFlowChat, ChatMessage } from '@/hooks/usePublicFlowChat';
import { ImageMessageBubble } from '@/components/tenant/chat/ImageMessageBubble';
import { VideoMessageBubble } from '@/components/tenant/chat/VideoMessageBubble';
import { AudioMessageBubble } from '@/components/tenant/chat/AudioMessageBubble';
import { DocumentMessageBubble } from '@/components/tenant/chat/DocumentMessageBubble';
import { OptionListMessageBubble } from '@/components/tenant/chat/OptionListMessageBubble';
import { ButtonListMessageBubble } from '@/components/tenant/chat/ButtonListMessageBubble';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';
import { cn } from '@/lib/utils';
import { injectAllTracking } from '@/lib/trackingInjector';
import { captureUTMParams, pushUTMToDataLayer } from '@/lib/utmCapture';

/**
 * Componente de mensagem individual com suporte a templates e mídia
 */
function MessageBubble({
  message,
  config,
  onButtonClick,
}: {
  message: ChatMessage;
  config: ReturnType<typeof usePublicFlowChat>['config'];
  onButtonClick?: (buttonId: string, buttonText: string) => void;
}) {
  // Estado para controle do lightbox de mídia
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  const isBot = message.type === 'bot';
  const isSystem = message.type === 'system';
  
  // Extrair configurações de cores
  const userBubble = config?.user_bubble_color || config?.primary_color || '#3b82f6';
  const botBubble = config?.bot_bubble_color || '#f3f4f6';
  const userText = config?.user_text_color || '#ffffff';
  const botText = config?.bot_text_color || '#1f2937';
  const borderRadius = config?.border_radius || '16px';
  
  // Detecção inteligente de estilo WhatsApp (não apenas template_id)
  const isWhatsApp = config?.template_id === 'whatsapp' || 
    config?.background_image?.includes('background-whatsapp') ||
    ['#075E54', '#128C7E', '#25D366'].includes(config?.header_bg_color?.toUpperCase() || '');
  
  const direction = isBot ? 'inbound' : 'outbound';

  // Mensagem de sistema
  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <span 
          className="text-xs px-3 py-1 rounded-full"
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.1)',
            color: '#667781'
          }}
        >
          {message.content}
        </span>
      </div>
    );
  }

  // Função para renderizar conteúdo baseado no tipo de mídia
  const renderMessageContent = () => {
    // Renderiza mídia se existir
    switch (message.mediaType) {
      case 'image':
        return (
          <>
            <ImageMessageBubble
              url={message.mediaUrl || ''}
              caption={message.caption}
              direction={direction}
              onOpenLightbox={() => setLightboxOpen(true)}
            />
            <ImageLightbox
              open={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
              imageUrl={message.mediaUrl || ''}
            />
          </>
        );
      
      case 'video':
        return (
          <>
            <div 
              className="cursor-pointer"
              onClick={() => setLightboxOpen(true)}
            >
              <VideoMessageBubble
                url={message.mediaUrl || ''}
                caption={message.caption}
                duration={message.duration}
                direction={direction}
              />
            </div>
            <ImageLightbox
              open={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
              mediaItems={[{ url: message.mediaUrl || '', type: 'video' }]}
            />
          </>
        );
      
      case 'audio':
        return (
          <AudioMessageBubble
            url={message.mediaUrl || ''}
            duration={message.duration}
            direction={direction}
          />
        );
      
      case 'document':
        return (
          <DocumentMessageBubble
            url={message.mediaUrl || ''}
            filename={message.filename || 'documento'}
            mimeType={message.mimeType}
            direction={direction}
          />
        );
      
      case 'embed':
        // Container do embed - centralizado com display block para eliminar gap inferior
        return (
          <div className="w-full rounded-xl overflow-hidden shadow-md flex justify-center">
            <iframe
              src={normalizeEmbedUrl(message.mediaUrl || '')}
              style={{ width: '100%', height: `${message.embedHeight || 400}px`, border: 'none', display: 'block' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Conteúdo incorporado"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />
          </div>
        );

      case 'list':
        return (
          <OptionListMessageBubble
            content={message.content}
            title={message.listTitle}
            options={message.listOptions || []}
            direction={direction}
            variant={isWhatsApp ? 'whatsapp' : 'default'}
            onOptionClick={(optionId, optionLabel) => onButtonClick?.(optionId, optionLabel)}
          />
        );
      
      case 'buttons':
        // Usa ButtonListMessageBubble para renderização consistente
        if (message.buttons && message.buttons.length > 0) {
          return (
            <ButtonListMessageBubble
              content={message.content}
              options={message.buttons.map(b => ({ id: b.id, label: b.text }))}
              direction={direction}
              variant={isWhatsApp ? 'whatsapp' : 'default'}
              onOptionClick={(optionId, optionLabel) => onButtonClick?.(optionId, optionLabel)}
            />
          );
        }
        // Fallback para texto simples
        return null;
      
      default:
        // Sem mediaType específico - verifica se tem botões
        if (message.buttons && message.buttons.length > 0) {
          return (
            <ButtonListMessageBubble
              content={message.content}
              options={message.buttons.map(b => ({ id: b.id, label: b.text }))}
              direction={direction}
              variant={isWhatsApp ? 'whatsapp' : 'default'}
              onOptionClick={(optionId, optionLabel) => onButtonClick?.(optionId, optionLabel)}
            />
          );
        }
        // Texto simples
        return null;
    }
  };

  const mediaContent = renderMessageContent();

  // Container da mensagem com animação suave de entrada (fade + slide up)
  return (
    <div
      className={`flex items-end gap-2 mb-2 ${isBot ? 'justify-start' : 'justify-end'}`}
      style={{ animation: `bubbleFadeIn ${isBot ? '400ms' : '300ms'} ease-out forwards` }}
    >
      {/* Avatar do bot - usa primary_color para consistência com botão */}
      {isBot && config?.bot_avatar_enabled && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          {config.bot_avatar_url ? (
            <AvatarImage src={config.bot_avatar_url} />
          ) : null}
          <AvatarFallback 
            style={{ 
              backgroundColor: `${config.primary_color}20`,
            }}
          >
            <Bot className="h-4 w-4" style={{ color: config.primary_color }} />
          </AvatarFallback>
        </Avatar>
      )}

      {/* Conteúdo da mensagem */}
      {/* Conteúdo da mensagem - largura condicional: bot com mídia = mais largo, bot texto = médio, user = padrão */}
      <div className={`flex flex-col gap-2 ${isBot ? (mediaContent ? 'max-w-[100%]' : 'max-w-[100%] sm:max-w-[90%]') : 'max-w-[75%]'} ${isBot ? '' : 'items-end'}`}>
        {/* Se tem mídia/botões/lista, renderiza o componente específico */}
        {mediaContent ? (
          // Container com setinha para mídia/botões quando WhatsApp
          <div className={cn(
            "relative",
            isWhatsApp && isBot && 'whatsapp-tail-left'
          )}>
            {mediaContent}
          </div>
        ) : (
          // Texto simples sem mídia
          <div
            className={cn(
              "px-4 py-2 text-[16px] leading-relaxed shadow-sm transition-all duration-300 relative",
              isWhatsApp && isBot && 'whatsapp-tail-left',
              isWhatsApp && !isBot && 'whatsapp-tail-right'
            )}
            style={{
              backgroundColor: isBot ? botBubble : userBubble,
              color: isBot ? botText : userText,
              borderRadius: borderRadius,
              borderBottomRightRadius: !isBot ? '4px' : borderRadius,
              borderBottomLeftRadius: isBot ? '4px' : borderRadius,
              // Padding extra para acomodar timestamp e check nas mensagens do usuário (WhatsApp)
              paddingBottom: isWhatsApp && !isBot ? '18px' : undefined,
            }}
          >
            <p className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: formatWhatsAppText(message.content) }} />
            
            {/* Indicador de leitura (timestamp + ✓✓) para mensagens do usuário no WhatsApp */}
            {isWhatsApp && !isBot && (
              <span className="absolute bottom-1 right-2 flex items-center gap-0.5">
                <span className="text-[10px] text-gray-500 mr-1">
                  {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
                {/* Duplo check verde - simulando "lida" */}
                <svg 
                  viewBox="0 0 16 11" 
                  height="11" 
                  width="16" 
                  className="text-[#53bdeb]"
                  fill="currentColor"
                >
                  <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.047.25.14.343l2.996 2.996a.724.724 0 0 0 .241.178.732.732 0 0 0 .298.064.727.727 0 0 0 .559-.267l6.664-8.253a.46.46 0 0 0 .102-.355.493.493 0 0 0-.178-.381l-.31-.305z" />
                  <path d="M14.757.653a.457.457 0 0 0-.305-.102.493.493 0 0 0-.38.178l-6.19 7.636-1.156-1.093-.31.31 1.689 1.689a.724.724 0 0 0 .241.178.732.732 0 0 0 .298.064.727.727 0 0 0 .559-.267l6.664-8.253a.46.46 0 0 0 .102-.355.493.493 0 0 0-.178-.381l-.31-.305z" />
                </svg>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Avatar do usuário (não mostrar no WhatsApp) */}
      {!isBot && config?.user_avatar_enabled && !isWhatsApp && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarFallback className="bg-muted">
            <User className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
}

/**
 * Indicador de digitação animado
 */
function TypingIndicator({ config }: { config: ReturnType<typeof usePublicFlowChat>['config'] }) {
  const botBubble = config?.bot_bubble_color || '#f3f4f6';
  const borderRadius = config?.border_radius || '16px';
  // Detecção inteligente de estilo WhatsApp
  const isWhatsApp = config?.template_id === 'whatsapp' || 
    config?.background_image?.includes('background-whatsapp') ||
    ['#075E54', '#128C7E', '#25D366'].includes(config?.header_bg_color?.toUpperCase() || '');
  
  // Cor dos pontos de digitação por plataforma
  const getTypingDotColor = () => {
    switch (config?.template_id) {
      case 'whatsapp': return '#667781';
      case 'instagram': return '#8e8e8e';
      case 'messenger': return '#0084ff';
      case 'tiktok': return '#fe2c55';
      case 'telegram': return '#0088cc';
      default: return '#9ca3af';
    }
  };

  const dotColor = getTypingDotColor();

  return (
    <div className="flex items-end gap-2 justify-start mb-4">
      {/* Avatar do bot para templates que mostram avatar - usa primary_color */}
      {config?.bot_avatar_enabled && (
        <Avatar className="h-8 w-8">
          {config.bot_avatar_url ? (
            <AvatarImage src={config.bot_avatar_url} />
          ) : null}
          <AvatarFallback 
            style={{ 
              backgroundColor: `${config.primary_color}20`,
            }}
          >
            <Bot className="h-4 w-4" style={{ color: config.primary_color }} />
          </AvatarFallback>
        </Avatar>
      )}
      <div
        className="px-4 py-3 text-sm shadow-sm flex items-center gap-1.5"
        style={{
          backgroundColor: botBubble,
          borderRadius: borderRadius,
          borderBottomLeftRadius: '4px',
        }}
      >
        <span 
          style={{ 
            width: '8px', 
            height: '8px', 
            borderRadius: '50%', 
            backgroundColor: dotColor,
            display: 'inline-block',
            animation: 'typingBounce 1.4s infinite ease-in-out',
            animationDelay: '0ms'
          }} 
        />
        <span 
          style={{ 
            width: '8px', 
            height: '8px', 
            borderRadius: '50%', 
            backgroundColor: dotColor,
            display: 'inline-block',
            animation: 'typingBounce 1.4s infinite ease-in-out',
            animationDelay: '200ms'
          }} 
        />
        <span 
          style={{ 
            width: '8px', 
            height: '8px', 
            borderRadius: '50%', 
            backgroundColor: dotColor,
            display: 'inline-block',
            animation: 'typingBounce 1.4s infinite ease-in-out',
            animationDelay: '400ms'
          }} 
        />
      </div>
    </div>
  );
}

/**
 * Header do chat - Full-width responsivo
 * WhatsApp autêntico ou padrão conforme template
 */
function ChatHeader({ 
  config, 
  isTyping,
  tenantName 
}: { 
  config: ReturnType<typeof usePublicFlowChat>['config'];
  isTyping: boolean;
  tenantName?: string;
}) {
  const headerBg = config?.header_bg_color || config?.primary_color || '#3b82f6';
  const headerText = config?.header_text_color || '#ffffff';
  // Detecção inteligente de estilo WhatsApp
  const isWhatsApp = config?.template_id === 'whatsapp' || 
    config?.background_image?.includes('background-whatsapp') ||
    ['#075E54', '#128C7E', '#25D366'].includes(config?.header_bg_color?.toUpperCase() || '');

  // Texto de digitação por plataforma
  const getTypingText = () => {
    switch (config?.template_id) {
      case 'whatsapp': return 'digitando...';
      case 'telegram': return 'digitando...';
      default: return 'Digitando...';
    }
  };

  if (isWhatsApp) {
    // Header estilo WhatsApp autêntico - full-width
    return (
      <div 
        className="w-full flex items-center gap-2 px-3 py-2 transition-colors duration-300"
        style={{ backgroundColor: headerBg }}
      >
        {/* Seta voltar */}
        <ArrowLeft className="h-5 w-5 cursor-pointer" style={{ color: headerText }} />
        
        {/* Avatar circular - só mostra se bot_avatar_enabled */}
        {config?.bot_avatar_enabled && (
          <Avatar className="h-10 w-10 border-2 border-white/20">
            {config.bot_avatar_url ? (
              <AvatarImage src={config.bot_avatar_url} />
            ) : config.company_logo_url ? (
              <AvatarImage src={config.company_logo_url} />
            ) : null}
            <AvatarFallback 
              style={{ 
                backgroundColor: `${config.primary_color}20`,
              }}
            >
              <Bot className="h-5 w-5" style={{ color: config.primary_color }} />
            </AvatarFallback>
          </Avatar>
        )}
        
        {/* Nome + status online/digitando */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm truncate" style={{ color: headerText }}>
            {config?.company_name || tenantName || 'Assistente Virtual'}
          </h3>
          <p className="text-xs" style={{ color: headerText, opacity: 0.85 }}>
            {isTyping ? getTypingText() : 'online'}
          </p>
        </div>
        
        {/* Ícones direita (vídeo, telefone, menu) */}
        <div className="flex items-center gap-4">
          <Video className="h-5 w-5 cursor-pointer" style={{ color: headerText }} />
          <Phone className="h-5 w-5 cursor-pointer" style={{ color: headerText }} />
          <MoreVertical className="h-5 w-5 cursor-pointer" style={{ color: headerText }} />
        </div>
      </div>
    );
  }

  // Header padrão para outros templates - full-width
  return (
    <div 
      className="w-full p-4 flex items-center gap-3 border-b transition-colors duration-300"
      style={{ backgroundColor: headerBg }}
    >
      {config?.company_logo_url ? (
        <img 
          src={config.company_logo_url} 
          alt="Logo" 
          className="h-10 w-10 rounded-full object-cover bg-white/20"
        />
      ) : (
        <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
          <Building2 className="h-5 w-5" style={{ color: headerText }} />
        </div>
      )}
      <div className="flex-1">
        <h3 className="font-semibold" style={{ color: headerText }}>
          {config?.company_name || tenantName || 'Empresa'}
        </h3>
        <p className="text-xs" style={{ color: headerText, opacity: 0.8 }}>
          {isTyping ? getTypingText() : 'Online agora'}
        </p>
      </div>
    </div>
  );
}

/**
 * Input inline estilo Typebot - aparece após mensagens quando aguardando resposta
 */
function InlineInput({
  config,
  inputValue,
  setInputValue,
  onSend,
  disabled,
  inputRef,
}: {
  config: ReturnType<typeof usePublicFlowChat>['config'];
  inputValue: string;
  setInputValue: (value: string) => void;
  onSend: () => void;
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  const primaryColor = config?.primary_color || (config?.template_id === 'whatsapp' ? config?.header_bg_color : '#5BBFBA') || '#5BBFBA';
  
  // Tipografia moderna Inter
  const fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

  return (
    <div className="flex items-center gap-3 mt-3 mb-4 justify-end">
      {/* Espaçamento para alinhar com avatar das mensagens (32px avatar + 8px gap) */}
      {config?.bot_avatar_enabled && (
        <div className="w-8 shrink-0 hidden sm:block" />
      )}
      {/* Input + Botão Enviar - estilo LimpaCRM/Typebot */}
      <div className="flex items-center gap-3 w-full sm:w-auto" style={{ marginLeft: config?.bot_avatar_enabled ? '40px' : '0' }}>
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && onSend()}
          disabled={disabled}
          placeholder="Digite sua resposta..."
          className="flex-1 sm:flex-none sm:w-72 px-5 py-3 bg-white border 
                     shadow-sm outline-none transition-all duration-200 disabled:opacity-50
                     focus:ring-2 focus:ring-offset-0"
          style={{
            borderRadius: '9999px',
            borderColor: '#E5E7EB',
            fontFamily,
            fontSize: '16px', // Mínimo 16px para evitar zoom automático no iOS
          }}
        />
        <Button
          onClick={onSend}
          disabled={!inputValue.trim() || disabled}
          className="shrink-0 px-6 py-3 text-sm font-semibold text-white shadow-sm 
                     hover:opacity-90 transition-opacity"
          style={{
            backgroundColor: primaryColor,
            borderRadius: '9999px',
            height: '48px',
            fontFamily,
            opacity: 1,
          }}
        >
          Enviar
        </Button>
      </div>
    </div>
  );
}

/**
 * Página principal do chat público
 * Layout: Header full-width + Mensagens centralizadas + Input inline
 */
export default function PublicFlowChat() {
  // Extrai tenant_slug e flow_slug da URL hierárquica
  const { tenantSlug, flowSlug } = useParams<{ tenantSlug: string; flowSlug: string }>();
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null); // Âncora para scroll automático

  const {
    flowData,
    messages,
    isLoading,
    isTyping,
    error,
    isCompleted,
    waitingForInput,
    sendUserMessage,
    config,
    tracking,
  } = usePublicFlowChat(tenantSlug, flowSlug);

  // Injeta scripts de rastreamento e captura UTMs quando dados do fluxo carregam
  useEffect(() => {
    if (tracking && flowData) {
      injectAllTracking(tracking, {
        event: 'flow_loaded',
        flow_id: flowData.flow.id,
        flow_name: flowData.flow.name,
        tenant_name: flowData.tenant_name,
      });
    }

    // Captura parâmetros UTM da URL e propaga para o dataLayer
    captureUTMParams();
    pushUTMToDataLayer();
  }, [tracking, flowData]);

  // Extrair configurações
  const backgroundColor = config?.background_color || '#f3f4f6';
  const backgroundImage = config?.background_image;

  // Auto scroll para última mensagem - usa scrollIntoView para garantir visibilidade
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end' 
        });
      }
    };
    
    // Delay para garantir que o DOM foi atualizado com as novas mensagens
    const timeoutId = setTimeout(scrollToBottom, 100);
    
    return () => clearTimeout(timeoutId);
  }, [messages, isTyping, waitingForInput]);

  // Foca no input quando aguardando resposta
  useEffect(() => {
    if (waitingForInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [waitingForInput]);

  // Handler de envio de mensagem
  const handleSend = () => {
    if (!inputValue.trim() || !waitingForInput || isCompleted) return;
    sendUserMessage(inputValue.trim());
    setInputValue('');
  };

  // Handler de clique em botão
  const handleButtonClick = (buttonId: string, buttonText: string) => {
    sendUserMessage(buttonText, buttonId);
  };

  // Estado de loading
  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#f5f5f5' }}
      >
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Estado de erro
  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#f5f5f5' }}
      >
        <div className="text-center max-w-md p-6">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
          <h1 className="text-xl font-semibold mb-2">Fluxo não disponível</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor }}
    >
      {/* CSS de animação para digitação e setinhas WhatsApp */}
      <style>
        {`
          /* Animação suave de entrada dos balões - fade + slide up + scale */
          @keyframes bubbleFadeIn {
            from {
              opacity: 0;
              transform: translateY(10px) scale(0.97);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes typingBounce {
            0%, 60%, 100% {
              transform: translateY(0);
              opacity: 0.4;
            }
            30% {
              transform: translateY(-4px);
              opacity: 1;
            }
          }
          
          /* Setinha/tail das bolhas WhatsApp - lado esquerdo (bot) */
          .whatsapp-tail-left::before {
            content: '';
            position: absolute;
            left: -8px;
            bottom: 0;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0 0 10px 10px;
            border-color: transparent transparent #ffffff transparent;
          }
          
          /* Setinha/tail das bolhas WhatsApp - lado direito (user) */
          .whatsapp-tail-right::after {
            content: '';
            position: absolute;
            right: -8px;
            bottom: 0;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0 10px 10px 0;
            border-color: transparent #DCF8C6 transparent transparent;
          }
        `}
      </style>

      {/* CSS customizado do usuário */}
      {config?.custom_css && (
        <style dangerouslySetInnerHTML={{ __html: config.custom_css }} />
      )}

      {/* Header FULL-WIDTH - fora do container centralizado */}
      <ChatHeader 
        config={config} 
        isTyping={isTyping}
        tenantName={flowData?.tenant_name}
      />

      {/* Área de mensagens com scroll - centralizado */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto transition-colors duration-300"
        style={{ 
          backgroundColor,
          // Imagem de fundo quando configurada (WhatsApp)
          ...(backgroundImage && {
            backgroundImage: `url(${backgroundImage})`,
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto',
          }),
        }}
      >
        {/* Container centralizado apenas para mensagens */}
        <div 
          className="mx-auto px-4 py-4"
          style={{ maxWidth: config?.max_width || '900px' }}
        >
          {/* Lista de mensagens */}
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              config={config}
              onButtonClick={handleButtonClick}
            />
          ))}
          
          {/* Indicador de digitação */}
          {isTyping && <TypingIndicator config={config} />}
          
          {/* INPUT INLINE estilo Typebot - aparece após mensagens quando aguardando input de TEXTO */}
          {/* Não exibe quando aguardando clique em botão ou seleção de lista */}
          {(() => {
            // Verifica se última mensagem do bot é do tipo botão ou lista
            const lastBotMessage = [...messages].reverse().find(m => m.type === 'bot');
            const isAwaitingButtonOrList = lastBotMessage?.mediaType === 'buttons' || 
                                           lastBotMessage?.mediaType === 'list';
            
            // Só mostra input de texto quando aguardando E NÃO for botão/lista
            if (waitingForInput && !isCompleted && !isAwaitingButtonOrList) {
              return (
                <InlineInput
                  config={config}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  onSend={handleSend}
                  disabled={!waitingForInput || isCompleted}
                  inputRef={inputRef}
                />
              );
            }
            return null;
          })()}
          
          {/* Mensagem de finalização */}
          {isCompleted && (
            <div className="text-center py-4 text-muted-foreground text-sm">
              Conversa finalizada
            </div>
          )}
          
          {/* Âncora invisível para scroll automático - sempre no final */}
          <div ref={messagesEndRef} style={{ height: 1 }} />
        </div>
      </div>

      {/* Branding no rodapé (opcional) - full-width */}
      {config?.show_branding && (
        <div className="py-2 text-center bg-transparent">
          <p className="text-[10px] text-muted-foreground">
            Powered by{' '}
            <a
              href="https://uaizychat.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              UaizyChat
            </a>
          </p>
        </div>
      )}
    </div>
  );
}
